import {Dispatchable} from "../../stem-core/base/Dispatcher";
import {NavbarConstants} from "../Constants";
import {isSmallScreen} from "../../blinkpay/UtilsLib";
import {isSafari} from "../../stem-core/base/Utils";
import type {UIChild} from "../../stem-core/ui/UIBase";
import type {Section} from "../primitives/Section";


// The links across the top of the page
export interface NavLink {
    id: string;
    href: string;
    label: UIChild;
}

// A scrollable section of the page; `element` is the class, which tracks its own instances
export interface NavSection {
    // Any concrete Section subclass; Section is generic over its options, so it has to be instantiated here
    element: typeof Section<any>;
    id: string;
    label: UIChild;
}

export class NavManager extends Dispatchable {
    activeCallbacks: (() => void)[] = [];

    // Assigned in the constructor and by the scroll handlers - `declare` emits nothing.
    declare sections: NavLink[];
    declare items: NavSection[];
    declare activeTab?: string;
    declare isScrolling?: boolean;
    declare clickedElementId?: string | null;

    constructor(sections: NavLink[], items: NavSection[]) {
        super();

        this.sections = sections;
        this.items = items;

        this.attachEventListener(window, "scroll", () => {
            this.updateActiveSection();
        });
        this.attachEventListener(window, "resize", () => {
            this.updateActiveSection();
        });
    }

    getActiveItem() {
        const {items} = this;

        let itemToSelect = items[0];
        let bestOverlapWithScreen = 0;

        for (const item of items) {
            const itemElement = item.element.getInstance();
            if (!itemElement) {
                continue;
            }
            let {top, bottom, height} = itemElement.node.getBoundingClientRect();
            if (top < 0) {
                top = 0;
            }
            if (bottom > window.innerHeight) {
                bottom = window.innerHeight;
            }
            const currentOverlapWithScreen = (bottom - top) / height;
            if (currentOverlapWithScreen > bestOverlapWithScreen) {
                bestOverlapWithScreen = currentOverlapWithScreen;
                itemToSelect = item;
            }
        }

        return itemToSelect;
    }

    updateActiveSection() {
        const activeTabId = this.getActiveItem().id;

        if (this.isScrolling) {
            if (activeTabId === this.clickedElementId) {
                this.clickedElementId = null;
                this.isScrolling = false;
            }
        } else {
            this.updateActiveTab(activeTabId);
        }
    }

    updateActiveTab(activeTab: string) {
        this.activeTab = activeTab;
        this.dispatch("activeTabUpdate", activeTab);
    }

    getActiveTab() {
        return this.activeTab;
    }

    scrollTo(elementId: string) {
        const clickedSection = this.items.find(item => item.id === elementId);

        this.clickedElementId = clickedSection.id;

        // Flag to skip highlighting intermediary sections-publisher.
        this.isScrolling = true;

        const navbarScrollOffset = isSmallScreen() ? NavbarConstants.MOBILE_HEIGHT : NavbarConstants.HEIGHT;
        window.scrollTo({
            top: clickedSection.element.getInstance().node.offsetTop - navbarScrollOffset,
            behavior: "smooth",
        });

        if (isSafari()) {
            this.updateActiveSection();
        }
    }
}
